import { User } from "oidc-client-ts";

/**
 * Represents the profile structure for a Keycloak user.
 */
interface KeycloakProfile {
  tomorrowthings: {
    namespaces: string[];
  }[];
}

/**
 * Represents the profile structure for a Zitadel user.
 */
interface ZitadelProfile {
  "urn:zitadel:iam:user:metadata": {
    "tomorrowthings-customer-namespace": string;
  }[];
}

/**
 * Represents a user authenticated through Keycloak.
 */
export interface KeycloakUser {
  profile: KeycloakProfile;
}

/**
 * Represents a user authenticated through Zitadel.
 */
export interface ZitadelUser {
  profile: ZitadelProfile;
}

/**
 * Represents an authenticated user, which can be either a KeycloakUser or a ZitadelUser.
 */
type AuthenticatedUser = KeycloakUser | ZitadelUser;

/**
 * Default customer namespace value.
 */
const DEFAULT_CUSTOMER_NAMESPACE = "c1334236-ca47-4947-9eff-f29351d43127";

/**
 * A class responsible for extracting customer namespaces from authenticated users.
 */
export class CustomerNamespaceExtractor {
  private strategy: (user: User | AuthenticatedUser) => string;
  private strategyDictionary: {
    [key: string]: (user: User | AuthenticatedUser) => string;
  };

  /**
   * Creates an instance of CustomerNamespaceExtractor.
   * @constructor
   */
  constructor() {
    const defaultStrategy = this.extractCustomerNamespaceFromUserFromZitadel;

    this.strategy = defaultStrategy;

    this.strategyDictionary = {
      keycloak: this.extractCustomerNamespaceFromUserFromKeycloak,
      zitadel: this.extractCustomerNamespaceFromUserFromZitadel,
      static: this.extractDefaultCustomerNamespaceFromUser,
      default: defaultStrategy,
    };
  }

  /**
   * Sets the strategy for extracting customer namespaces.
   * @param {string} strategyName The name of the strategy.
   */
  public setStrategy(strategyName: string): void {
    const strategy = this.strategyDictionary[strategyName];

    if (!strategy) {
      console.warn(
        `Invalid strategy '${strategyName}'. Using default strategy.`,
      );
    }

    this.strategy = strategy || this.strategyDictionary["default"];
  }

  /**
   * Decodes a base64 encoded string.
   * @param {string} base64encodedString The base64 encoded string to decode.
   * @returns {string} The decoded string.
   */
  private static decodeBase64String(base64encodedString: string): string {
    return Buffer.from(base64encodedString, "base64").toString("utf-8");
  }

  /**
   * Extracts the customer namespace from a Zitadel user.
   * @param {ZitadelUser} user The Zitadel user object.
   * @returns {string} The extracted customer namespace.
   */
  public extractCustomerNamespaceFromUserFromZitadel(
    user: ZitadelUser,
  ): string {
    const profileMetadataKey = "urn:zitadel:iam:user:metadata";
    const namespaceKey = "tomorrowthings-customer-namespace";

    const metadata = user?.profile?.[profileMetadataKey]?.[0];
    const namespace = metadata?.[namespaceKey] || "";

    return namespace
      ? CustomerNamespaceExtractor.decodeBase64String(namespace)
      : "";
  }

  /**
   * Extracts the customer namespace from a Keycloak user.
   * @param {KeycloakUser} user The Keycloak user object.
   * @returns {string} The extracted customer namespace.
   */
  public extractCustomerNamespaceFromUserFromKeycloak(
    user: KeycloakUser,
  ): string {
    const namespaces = user?.profile?.tomorrowthings?.[0]?.namespaces;
    const namespace = namespaces?.[0];

    return namespace ? namespace.trim() : "";
  }

  /**
   * Extracts the default customer namespace.
   * @param {AuthenticatedUser} _user The authenticated user object (not used).
   * @returns {string} The default customer namespace.
   */
  public extractDefaultCustomerNamespaceFromUser(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _user: AuthenticatedUser,
  ): string {
    return DEFAULT_CUSTOMER_NAMESPACE;
  }

  /**
   * Extracts the customer namespace from an authenticated user.
   * @param {User | AuthenticatedUser} user The authenticated user object. This needs to be any!
   * @returns {string} The extracted customer namespace.
   */
  public extractCustomerNamespaceFromUser(
    user: User | AuthenticatedUser,
  ): string {
    return this.strategy(user);
  }
}

// Example usage
if (require.main === module) {
  const keycloakUser: KeycloakUser = {
    profile: {
      tomorrowthings: [
        {
          namespaces: ["c1334236-ca47-4947-9eff-f29351d43127"],
        },
      ],
    },
  };

  const zitadelUser: ZitadelUser = {
    profile: {
      "urn:zitadel:iam:user:metadata": [
        {
          "tomorrowthings-customer-namespace":
            "YzEzMzQyMzYtY2E0Ny00OTQ3LTllZmYtZjI5MzUxZDQzMTI3",
        },
      ],
    },
  };

  const extractor = new CustomerNamespaceExtractor();
  extractor.setStrategy("unknown");
  // Output: c1334236-ca47-4947-9eff-f29351d43127
  console.log(extractor.extractCustomerNamespaceFromUser(zitadelUser));

  extractor.setStrategy("keycloak");
  // Output: c1334236-ca47-4947-9eff-f29351d43127
  console.log(extractor.extractCustomerNamespaceFromUser(keycloakUser));

  extractor.setStrategy("static");
  // Output: c1334236-ca47-4947-9eff-f29351d43127
  console.log(extractor.extractCustomerNamespaceFromUser(keycloakUser));
}
